/**
 * Банер каталогу гостя (смуга слайдів над списком "Останні оновлення").
 * Дані — публічний GET /api/banners (налаштовуються в панелі підтримки, категорія
 * "Банери"; server: lib/banners.ts). Слайди перемикаються самі (інтервал із
 * налаштувань), свайпом і крапками; останній слайд плавно переходить у перший
 * (на кінці додається клон першого, після доїзду до нього — миттєвий стрибок на початок).
 */

export interface BannerSlideData {
  id: string;
  title: string;
  text: string;
  /** Опис при наведенні. */
  hint?: string;
  url: string;
  newTab: boolean;
  colorFrom: string;
  colorTo: string;
  angle: number;
}

export interface BannersPayload {
  enabled: boolean;
  intervalMs?: number;
  autoplay?: boolean;
  showOn?: 'updates' | 'all';
  /** Висота всіх банерів, rem. */
  heightRem?: number;
  slides?: BannerSlideData[];
}

export interface BannerController {
  /** Показаний (true) чи прихований (false) банер — у прихованому автоперемикання зупиняється. */
  setActive(active: boolean): void;
  destroy(): void;
}

let payloadPromise: Promise<BannersPayload> | null = null;

/** Одноразово на сторінку; будь-яка помилка → банерів нема (не ламаємо каталог). */
export function fetchBanners(): Promise<BannersPayload> {
  if (!payloadPromise) {
    payloadPromise = fetch('/api/banners', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? (r.json() as Promise<BannersPayload>) : { enabled: false }))
      .catch(() => ({ enabled: false }) as BannersPayload);
  }
  return payloadPromise;
}

const HEX = /^#[0-9a-f]{6}$/i;

function safeHref(url: string): string {
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : '';
  } catch {
    return '';
  }
}

function buildSlide(s: BannerSlideData): HTMLElement {
  const href = s.url ? safeHref(s.url) : '';
  const el = document.createElement(href ? 'a' : 'div');
  el.className = 'bslide';
  if (href) {
    (el as HTMLAnchorElement).href = href;
    if (s.newTab) {
      (el as HTMLAnchorElement).target = '_blank';
      (el as HTMLAnchorElement).rel = 'noopener';
    }
  }
  const from = HEX.test(s.colorFrom) ? s.colorFrom : '#3b5bdb';
  const to = HEX.test(s.colorTo) ? s.colorTo : '#1b2a6b';
  const angle = Number.isFinite(s.angle) ? s.angle : 135;
  const gradient = `linear-gradient(${angle}deg, ${from}, ${to})`;
  el.style.background = gradient;
  if (s.title) {
    const b = document.createElement('b');
    b.textContent = s.title;
    el.appendChild(b);
  }
  if (s.text) {
    const span = document.createElement('span');
    span.textContent = s.text;
    el.appendChild(span);
  }
  if (s.hint) {
    // Опис, що висвічується поверх банера при наведенні (або поки його утримують пальцем) — див. .btip в style.css.
    const tip = document.createElement('div');
    tip.className = 'btip';
    tip.textContent = s.hint;
    el.appendChild(tip);
  }
  return el;
}

export function mountBanner(host: HTMLElement, cfg: BannersPayload): BannerController | null {
  const data = cfg.slides ?? [];
  if (!cfg.enabled || !data.length) return null;

  host.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'banner';
  const track = document.createElement('div');
  track.className = 'banner-track';
  const dotsEl = document.createElement('div');
  dotsEl.className = 'bdots';
  box.append(track, dotsEl);
  host.appendChild(box);

  const real = data.map(buildSlide);
  const N = real.length;
  real.forEach((el) => track.appendChild(el));
  const multi = N > 1;
  if (multi) {
    const clone = real[0].cloneNode(true) as HTMLElement;
    clone.setAttribute('aria-hidden', 'true');
    clone.tabIndex = -1;
    track.appendChild(clone);
  }

  const dots: HTMLButtonElement[] = [];
  if (multi) {
    for (let i = 0; i < N; i++) {
      const d = document.createElement('button');
      d.className = 'bdot';
      d.setAttribute('aria-label', `Банер ${i + 1}`);
      dotsEl.appendChild(d);
      dots.push(d);
    }
  } else {
    dotsEl.style.display = 'none';
  }

  const INTERVAL = Math.max(2000, cfg.intervalMs ?? 10000);
  // Автоперемикання вмикає/вимикає адмін у налаштуваннях. Раніше воно мовчки вимикалось, коли в ОС
  // увімкнено "зменшити анімацію", а індикатор при цьому малювався ПОВНИМ — виглядало як зламаний
  // таймер. Тепер слайди перемикаються завжди, а "зменшена анімація" лише робить перехід миттєвим
  // (без плавного прокручування) і прибирає смугу заповнення в крапці (див. style.css).
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = !!cfg.autoplay && multi;
  const heightRem = Number(cfg.heightRem);
  if (Number.isFinite(heightRem) && heightRem > 0) box.style.setProperty('--bh', `${heightRem}rem`);
  box.style.setProperty('--bms', `${INTERVAL}ms`);

  /*
   * Автоперемикання: ОДИН постійний таймер-"сторож" (кожні 250 мс), який ніколи не зупиняється й не
   * залежить від того, чи дійшла подія "відпустили/пішли" (touchend/mouseleave/…) — раніше пауза,
   * поставлена такою подією, могла лишитись назавжди, і слайди переставали перемикатись. Тепер:
   *  - nextAt — момент наступного автоперемикання; будь-яка зміна слайда (mark) і будь-який дотик
   *    відсувають його на повний INTERVAL;
   *  - поки на банері курсор (лише пристрої з hover), вкладка прихована, банер сховано чи він
   *    не має розміру — просто відсуваємо nextAt, нічого не "ставимо на паузу" вручну.
   */
  let idx = 0;
  let nextAt = Date.now() + INTERVAL;
  let active = true;
  let mouseOver = false; // курсор МИШІ над банером (лише pointerType 'mouse' — дотик "залипаючого" hover не дає)
  let hovering = false;
  let touchingUntil = 0; // поки палець на банері — автоперемикання не чіпає слайд (до цього моменту; запобіжник на випадок втраченого touchend)
  let watchdog: ReturnType<typeof setInterval> | null = null;
  let resetTimer: ReturnType<typeof setTimeout> | undefined;
  let scrollTimer: ReturnType<typeof setTimeout> | undefined;

  function mark(i: number) {
    idx = i;
    nextAt = Date.now() + INTERVAL;
    dots.forEach((d, k) => {
      d.classList.remove('active');
      if (k === i) {
        void d.offsetWidth; // перезапуск CSS-анімації заповнення крапки
        d.classList.add('active');
      }
    });
  }
  function go(i: number, smooth = true) {
    clearTimeout(resetTimer); // застарілий "стрибок на початок" від попереднього переходу не має спрацювати після нового
    track.scrollTo({ left: i * track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    mark(i % N);
    if (i === N) {
      resetTimer = setTimeout(() => track.scrollTo({ left: 0, behavior: 'auto' }), 700);
    }
  }
  /** Дотик/колесо/клавіатура — користувач взаємодіє: відсуваємо автоперемикання на повний інтервал. */
  function touched() {
    nextAt = Date.now() + INTERVAL;
    if (multi) mark(idx);
  }
  function tick() {
    if (!active || !multi) return;
    if (document.hidden || !track.clientWidth) {
      nextAt = Date.now() + INTERVAL;
      return;
    }
    if (mouseOver !== hovering) {
      hovering = mouseOver;
      box.classList.toggle('paused', hovering);
      if (!hovering) mark(idx); // курсор пішов — індикатор і відлік стартують заново
    }
    if (hovering || Date.now() < touchingUntil) {
      nextAt = Date.now() + INTERVAL;
      return;
    }
    if (autoplay && Date.now() >= nextAt) go(idx + 1);
  }

  if (multi) {
    track.addEventListener('pointerdown', touched);
    // Дотик: поки палець тримається (навіть довше за інтервал) — слайд не перемикається "з-під пальця";
    // після відпускання/скасування (touchend/touchcancel) відлік іде заново. 15 с — запобіжник, якщо
    // браузер так і не надіслав touchend.
    track.addEventListener('touchstart', () => {
      touchingUntil = Date.now() + 15000;
      touched();
    }, { passive: true });
    const touchOff = () => {
      touchingUntil = 0;
      nextAt = Date.now() + INTERVAL;
    };
    track.addEventListener('touchend', touchOff, { passive: true });
    track.addEventListener('touchcancel', touchOff, { passive: true });
    track.addEventListener('wheel', touched, { passive: true });
    // Пауза при наведенні — за подіями вказівника, а не за :hover: на сенсорних екранах :hover
    // "залипає" після дотику (і банер здавався зламаним, бо назавжди стояв на паузі).
    box.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') mouseOver = true;
    });
    box.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') mouseOver = false;
    });
    window.addEventListener('blur', () => {
      mouseOver = false;
    });

    // Гортання мишкою на ПК: touch/перо гортаються нативно, а мишка — ні, тож тягнемо самі.
    // Перетягування починається ЛИШЕ коли мишка зсунулась більш ніж на 4 px: звичайний клік
    // (без руху) нічого не прокручує й не чіпає перехід, що саме відбувається (раніше клік
    // посеред автоперемикання скасовував його й відкочував слайд). Після відпускання докручуємо
    // до найближчого слайда (поріг 12% ширини, щоб короткий "ривок" теж перемикав); перетягування
    // не має відкривати посилання банера.
    let press: { x: number; left: number; id: number } | null = null; // кнопку натиснуто, рух ще не почався
    let drag: { x: number; left: number } | null = null; // перетягування триває
    let suppressClick = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      press = { x: e.clientX, left: track.scrollLeft, id: e.pointerId };
    });
    track.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.buttons === 0) {
        // кнопку вже відпущено (pointerup міг загубитись поза вікном) — не лишаємось у "перетягуванні"
        stopDrag(e.clientX);
        return;
      }
      if (!drag && press && Math.abs(e.clientX - press.x) > 4) {
        drag = { x: press.x, left: track.scrollLeft };
        clearTimeout(resetTimer);
        track.style.scrollSnapType = 'none';
        track.classList.add('dragging');
        track.scrollTo({ left: track.scrollLeft, behavior: 'auto' }); // зупиняємо плавну прокрутку, що ще триває
        try {
          track.setPointerCapture(press.id);
        } catch {
          /* не критично */
        }
      }
      if (drag) track.scrollLeft = drag.left - (e.clientX - drag.x);
    });
    function stopDrag(clientX: number) {
      const d = drag;
      const p = press;
      press = null;
      drag = null;
      if (!d) return; // це був звичайний клік — нічого не чіпаємо
      track.classList.remove('dragging');
      track.style.scrollSnapType = '';
      try {
        if (p) track.releasePointerCapture(p.id);
      } catch {
        /* не критично */
      }
      suppressClick = true;
      const w = track.clientWidth;
      if (!w) return;
      const dx = clientX - d.x;
      let target = Math.round(d.left / w);
      if (dx < -w * 0.12) target += 1;
      else if (dx > w * 0.12) target -= 1;
      go(Math.max(0, Math.min(N, target)));
    }
    track.addEventListener('pointerup', (e) => stopDrag(e.clientX));
    track.addEventListener('pointercancel', (e) => stopDrag(e.clientX));
    track.addEventListener('lostpointercapture', (e) => stopDrag(e.clientX));
    track.addEventListener('click', (e) => {
      if (!suppressClick) return;
      suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());

    track.addEventListener('scroll', () => {
      if (drag) return; // під час перетягування індикатор не смикаємо
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (!track.clientWidth) return;
        const c = Math.round(track.scrollLeft / track.clientWidth);
        if (c >= N) track.scrollTo({ left: 0, behavior: 'auto' });
        const m = c % N;
        if (m !== idx) mark(m);
      }, 80);
    });
    dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    mark(0);
    watchdog = setInterval(tick, 250);
  }
  const onResize = () => {
    if (track.clientWidth) track.scrollTo({ left: idx * track.clientWidth, behavior: 'auto' });
  };
  window.addEventListener('resize', onResize);

  return {
    setActive(next: boolean) {
      if (next === active) return;
      active = next;
      mouseOver = false; // сховали/показали — pointerleave міг не прийти
      if (next) {
        onResize();
        if (multi) mark(idx); // знову показали — відлік і індикатор із початку
      }
    },
    destroy() {
      if (watchdog) clearInterval(watchdog);
      clearTimeout(resetTimer);
      clearTimeout(scrollTimer);
      window.removeEventListener('resize', onResize);
      host.innerHTML = '';
    },
  };
}
